import { find, get } from "lodash";

import { omitTypename } from "src/util/graphql";

import {
  IMAGE_SOURCE_TYPES,
  LAYOUT_MODES,
} from "src/components/BannerImagePicker/consts";
import { ENTITY_ATTRIBUTE_TYPE } from "src/consts/entityAttribute";
import { SEARCH_LIMIT, unsplash } from "./BannerImagePicker.config";

/**
 * Get layout mode based on attribute type
 * @param {string} attributeType - ENTITY_ATTRIBUTE_TYPE value
 * @returns {string} - 'avatar' or 'background'
 */
export const getLayoutModeFromAttributeType = (attributeType) => {
  return attributeType === ENTITY_ATTRIBUTE_TYPE.IMAGE
    ? LAYOUT_MODES.AVATAR
    : LAYOUT_MODES.BACKGROUND;
};

export const getCropDataAttributeType = (attributeType) => {
  switch (attributeType) {
    case ENTITY_ATTRIBUTE_TYPE.BANNER_IMAGE:
      return ENTITY_ATTRIBUTE_TYPE.BANNER_IMAGE_CROP_DATA;
    case ENTITY_ATTRIBUTE_TYPE.IMAGE:
      return ENTITY_ATTRIBUTE_TYPE.IMAGE_CROP_DATA;
    default:
      throw new Error(`Invalid attribute type: ${attributeType}`);
  }
};

export const isCroppableAttributeType = (attributeType, selectedSourceType) => {
  return (
    [ENTITY_ATTRIBUTE_TYPE.BANNER_IMAGE, ENTITY_ATTRIBUTE_TYPE.IMAGE].includes(
      attributeType
    ) &&
    [
      IMAGE_SOURCE_TYPES.STOCK_IMAGES,
      IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
    ].includes(selectedSourceType)
  );
};

export const replaceImageWidth = (url, newWidth) => {
  if (!newWidth) {
    return url;
  }
  return url.replace(/w=\d+/, `w=${newWidth}`);
};

/**
 * @param {Object} selection
 * @param {string} [selection.url]
 * @param {Object} [selection.urls]
 * @param {string} [selection.urls.regular]
 * @param {number} [maxWidth]
 * @returns {string|null} - The image URL or null if not found.
 */
export const getImageUrl = (selection, maxWidth) => {
  if (selection?.url) {
    return selection.url;
  }

  if (selection?.urls?.regular) {
    if (maxWidth) {
      return replaceImageWidth(selection.urls.regular, maxWidth);
    }
    return selection.urls.regular;
  }

  return null;
};

export const unsplashSearch = async (query, page = 1) => {
  const result = await unsplash.search.getPhotos({
    query,
    page,
    perPage: SEARCH_LIMIT,
    orientation: "landscape",
    content_filter: "low",
    featured: true,
  });
  if (result.errors) {
    return { error: result.errors[0] };
  }
  return {
    data: get(result, "response.results", []),
  };
};

/**
 *
 * @param {Object} param
 * @param {teamform.attributes.BaseEntityAttribute[]} param.attributes
 * @param {Array<string>} param.attributeTypes
 * @returns {teamform.attributes.BaseEntityAttribute[]}
 */
export const resetAttributes = ({ attributes = [], attributeTypes = [] }) => {
  const matchedAttributes = attributes
    ?.filter((attr) => attributeTypes?.includes(attr?.attributeType))
    ?.map((attr) => ({
      ...attr,
      value: null,
    }));

  return omitTypename(matchedAttributes);
};

export const upsertAttribute = ({
  attributes = [],
  value,
  label,
  attributeType,
  subtype,
}) => {
  const imageToInsert = {
    label,
    value,
    attributeType,
    subtype,
  };
  const existingImageAttribute = find(attributes, { attributeType });

  if (!existingImageAttribute) {
    return omitTypename([imageToInsert]);
  }

  return omitTypename([
    {
      ...existingImageAttribute,
      ...imageToInsert,
    },
  ]);
};

export const getSourceTypeKey = (source) => {
  switch (source) {
    case "stock_images":
      return { imgUrl: "urls.regular", altKey: "alt_description" };
    case "company_images":
      return { imgUrl: "fileKey", altKey: "name" };
    case "color_picker":
      return { imgUrl: "color", altKey: "name" };
    default:
      return { imgUrl: "fileKey", altKey: "name" };
  }
};
